import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Shield, Zap, Star } from "lucide-react";
import { VturbPlayer } from "@/components/VturbPlayer";
import { WHATSAPP_CONFIG, HOTMART_CONFIG, PRICING, FINAL_CTA_VIDEOS } from "@/config/constants";

type Product = {
  id: string;
  title: string;
  subtitle?: string;
  price?: string;          // preço promocional
  priceOriginal?: string;  // preço antigo (riscado)
  priceX?: string;
  per?: string;
  cta?: string;
  href?: string;
  videoId?: string;
  videoUrl?: string;
  isMentoria?: boolean;
};


const PRODUCTS: Product[] = [
  {
    id: "metodo-lda",
    title: PRICING.metodoLda.name,
    subtitle: PRICING.metodoLda.description,
    cta: "Começar agora",
    href: HOTMART_CONFIG.metodoLda,
    videoId: FINAL_CTA_VIDEOS.metodoLda.id,
    videoUrl: FINAL_CTA_VIDEOS.metodoLda.url,
    priceOriginal: PRICING.metodoLda.priceOriginal,
    price: PRICING.metodoLda.price,
  },
  {
    id: "jornada-8-pilares",
    title: PRICING.jornada8Pilares.name,
    subtitle: PRICING.jornada8Pilares.description,
    cta: "Conhecer os 8 pilares",
    href: HOTMART_CONFIG.jornada8Pilares,
    videoId: FINAL_CTA_VIDEOS.jornada8Pilares.id,
    videoUrl: FINAL_CTA_VIDEOS.jornada8Pilares.url,
    priceOriginal: PRICING.jornada8Pilares.priceOriginal,
    price: PRICING.jornada8Pilares.price,
    priceX: `ou ${PRICING.jornada8Pilares.installments} de ${PRICING.jornada8Pilares.priceInstallment}`,
  },
  {
    id: "mentoria-individual",
    title: "Mentoria Individual",
    subtitle: "Atendimento 1:1 para acelerar seus resultados.",
    cta: "Converse conosco",
    href: WHATSAPP_CONFIG.mentoriaLink,
    videoId: FINAL_CTA_VIDEOS.mentoriaIndividual.id,
    videoUrl: FINAL_CTA_VIDEOS.mentoriaIndividual.url,
    isMentoria: true,
  },
];

export default function FinalCTA() {
  return (
    <section id="final-cta" className="relative py-20 md:py-28">
      {/* BG com grade suave + degradê */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(60%_60%_at_50%_30%,black,transparent)]"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/60 to-background" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />
      </div>

      <div className="container">
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">Escolha seu caminho</h2>
          <p className="mt-4 text-muted-foreground">
            Três caminhos, um objetivo: sua libertação das apostas. Assista aos vídeos e selecione o que faz sentido agora.
          </p>
        </div>

        {/* Grid de produtos */}
        <div className="grid gap-6 md:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, idx) => {
            const isFeatured = idx === 1; // produto do meio
            return (
              <Card
                key={p.id}
                className={[
                  "group relative overflow-hidden flex flex-col h-full border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 transition-all duration-300",
                  isFeatured
                    ? "border-transparent ring-2 ring-primary/60 shadow-xl shadow-primary/50 md:scale-[1.05]"
                    : "border-border/60 hover:shadow-lg",
                ].join(" ")}
              >
                {/* Glow diferenciado no destaque */}
                <div
                  aria-hidden
                  className={[
                    "pointer-events-none absolute inset-0 transition-opacity duration-500",
                    isFeatured
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100",
                  ].join(" ")}
                  style={{
                    background: isFeatured
                      ? "radial-gradient(800px 220px at 50% 0%, hsl(var(--primary)/0.14), transparent 65%)"
                      : "radial-gradient(600px 180px at 50% 0%, hsl(var(--primary)/0.12), transparent 60%)",
                  }}
                />

                {/* Faixa superior “Recomendado” */}
                {isFeatured && (
                  <div className="absolute left-0 right-0 top-0 z-10 h-9 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 backdrop-blur-sm border-b border-primary/20 flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="text-xs font-semibold uppercase tracking-widest">
                      Recomendado pela comunidade
                    </span>
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                )}

                <CardHeader className="relative pt-10">
                  <CardTitle className={isFeatured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}>
                    {p.title}
                  </CardTitle>
                  {p.subtitle ? <CardDescription className="mt-1">{p.subtitle}</CardDescription> : null}
                </CardHeader>

                <CardContent className="relative space-y-4 flex-1">
                  {/* Player YouTube Lite + selo MAIS VENDIDO (reposicionado) */}
                  <div className="relative w-full aspect-video rounded-xl overflow-hidden">
                    {p.videoId && p.videoUrl ? (
                      <VturbPlayer
                        videoId={p.videoId}
                        scriptUrl={p.videoUrl}
                        className="relative w-full rounded-xl"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center rounded-xl border border-border/60 bg-muted/20">
                        <div className="text-sm text-muted-foreground">Vídeo do produto ({p.title})</div>
                      </div>
                    )}

                    {/* Pílula MAIS VENDIDO — fora da faixa superior */}
                    {isFeatured && (
                      <div className="absolute left-3 bottom-3 z-20">
                        <div className="px-3 py-1 rounded-full text-[10px] md:text-xs font-extrabold tracking-widest text-primary-foreground bg-gradient-to-r from-primary to-[hsl(var(--accent))] shadow-md">
                          MAIS VENDIDO
                        </div>
                      </div>
                    )}
                  </div>

                  {idx === 0 ? (
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Diminuição imediata do desejo de apostar.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Reprogramação mental com técnicas de neurociência.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Primeiro passo real rumo à liberdade das apostas.</span>
                      </li>
                    </ul>
                  ) : null}

                  {idx === 1 ? (
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Diminuição imediata do desejo de apostar.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Reprogramação mental com técnicas de neurociência.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Primeiro passo real rumo à liberdade das apostas.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Imersão completa nos 8 pilares da libertação</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Mais de 38 exercícios práticos para fortalecer sua mente</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Transformação duradoura e proteção definitiva contra recaídas</span>
                      </li>
                    </ul>
                  ) : null}

                  {idx === 2 ? (
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Diminuição imediata do desejo de apostar.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Reprogramação mental com técnicas de neurociência.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Imersão completa nos 8 pilares da libertação</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Mais de 38 exercícios práticos para fortalecer sua mente</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Transformação duradoura e proteção definitiva contra recaídas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Atendimento individual e empático, feito sob medida para você</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Análise profunda dos seus gatilhos e comportamento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>Acompanhamento pessoal direto com especialistas</span>
                      </li>
                    </ul>
                  ) : null}

                  {/* Bullets */}
                  {/* <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 text-primary" />
                      <span>Conteúdo claro e objetivo para mudança real.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 text-primary" />
                      <span>Metodologia aplicada por especialistas.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 text-primary" />
                      <span>Acesso imediato após confirmação.</span>
                    </li>
                  </ul> */}

                  {/* Selo extra no destaque (custo-benefício) */}
                  {isFeatured && (
                    <div className="mt-2 text-[11px] md:text-xs flex items-center justify-center gap-2 text-primary font-semibold uppercase tracking-wider">
                      <div className="h-1 w-4 rounded bg-primary/60" />
                      MELHOR ESCOLHA
                      <div className="h-1 w-4 rounded bg-primary/60" />
                    </div>
                  )}
                </CardContent>

                <CardFooter className="relative z-[1] flex flex-col items-stretch gap-3 mt-auto pb-12">
                  {/* Preços: antigo riscado + promocional em destaque (sem amarelo) */}
                  {p.price || p.priceOriginal ? (
                    <div className="flex flex-col items-center justify-center mt-2 mb-1">
                      {p.priceOriginal ? (
                        <div className="text-sm text-muted-foreground line-through">{p.priceOriginal}</div>
                      ) : null}
                      {p.price ? (
                        <div
                          className={[
                            "text-3xl font-extrabold drop-shadow-[0_0_8px_hsl(var(--primary)/0.25)]",
                            isFeatured ? "text-primary" : "text-primary",
                          ].join(" ")}
                        >
                          {p.price}
                        </div>
                      ) : null}
                      {p.priceX ? (
                        <span className="text-sm text-muted-foreground mt-1">{p.priceX}</span>
                      ) : null}
                    </div>
                  ) : null}

                  {/* CTA */}
                  {p.href ? (
                    <Button
                      size="lg"
                      variant={isFeatured ? "finalcta" : "finalcta"}
                      className={[
                        "w-full",
                        isFeatured
                          ? "h-12 text-base font-bold shadow-lg shadow-primary/25 hover:shadow-primary/35 hover:translate-y-[-1px] transition-all"
                          : "",
                        p.isMentoria ? "mb-7" : "",
                      ].join(" ")}
                      asChild
                    >
                      <a
                        href={p.href}
                        target={p.isMentoria ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        aria-label={`${p.title} - ${p.cta ?? "Saiba mais"}`}
                      >
                        <Zap className="mr-2 h-4 w-4" />
                        {isFeatured ? "Quero os 8 pilares" : p.cta ?? "Quero saber mais"}
                      </a>
                    </Button>
                  ) : (
                    <Button size="lg" className="w-full" onClick={() => {
                      // Meta Pixel AddToCart event
                      if ((window as any).fbq) {
                        (window as any).fbq("track", "AddToCart", {
                          content_name: "LDA"+" "+p.id,
                          currency: "BRL",
                        });
                      }
                    }}>
                      <Zap className="mr-2 h-4 w-4" />
                      {p.cta ?? "Quero saber mais"}
                    </Button>
                  )}

                  {/* Garantia (não exibir na mentoria) */}
                  {!p.isMentoria ? (
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Shield className="h-4 w-4" />
                      <span>Garantia incondicional de 7 dias</span>
                    </div>
                  ) : null}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
